import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { UniversalPagination } from '../../common/UniversalPagination';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CallMerge as CallMergeIcon,
} from '@mui/icons-material';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
}

interface LibraryTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  onPageChange: (value: number) => void;
  limit: number;
  onLimitChange: (limit: number) => void;
  storageKey?: string;

  // Sorting
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (property: string) => void;

  // Actions
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  onMerge?: (item: T) => void;
  onRowClick?: (item: T) => void;
  /** Додаткові кнопки рядка (перед редагуванням). */
  extraActions?: (item: T) => React.ReactNode;

  // Helper to check if item is user-created (for merge/delete logic)
  isUserCreated: (item: T) => boolean;

  disableDelete?: boolean;
}

export function LibraryTable<T extends { id: number }>({
  data,
  columns,
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  limit,
  onLimitChange,
  storageKey,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
  onMerge,
  extraActions,
  onRowClick,
  isUserCreated,
  disableDelete,
}: LibraryTableProps<T>) {
  const createSortHandler = (property: string) => () => {
    onSort(property);
  };

  if (error) {
    return <Typography color="error" textAlign="center" sx={{ mt: 4 }}>{error}</Typography>;
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
  }

  if (data.length === 0) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Записів не знайдено.</Typography>;
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id as string}
                  align={column.align || 'left'}
                  sortDirection={sortBy === column.id ? sortOrder.toLowerCase() as 'asc' | 'desc' : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
                      onClick={createSortHandler(column.id as string)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow 
                hover 
                key={item.id} 
                onClick={() => onRowClick && onRowClick(item)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <TableCell key={column.id as string} align={column.align || 'left'}>
                    {column.render ? column.render(item) : (item[column.id as keyof T] as React.ReactNode)}
                  </TableCell>
                ))}
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {isUserCreated(item) && onMerge && (
                    <IconButton onClick={() => onMerge(item)} size="small">
                      <CallMergeIcon />
                    </IconButton>
                  )}
                  {extraActions?.(item)}
                  <IconButton onClick={() => onEdit(item)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => onDelete(item)} size="small" disabled={disableDelete}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UniversalPagination
        page={page}
        count={totalPages}
        limit={limit}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        storageKey={storageKey}
      />
    </>
  );
}
